import useAuth from '../auth/useAuth';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export function useFetch() {
  const { getAccessTokenSilently, handleAuthFailure } = useAuth();
  const fetcher = async ({ path, method, data, filters, headers, timeoutMs }) => {
    const token = await getAccessTokenSilently();

    const url = `${API_BASE_URL}api/${path}${
      filters ? `?${new URLSearchParams(filters).toString()}` : ''
    }`;

    const controller = timeoutMs ? new AbortController() : null;
    const timeoutId =
      timeoutMs && controller
        ? setTimeout(() => {
            controller.abort();
          }, timeoutMs)
        : null;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...headers,
        },
        method,
        body: data,
        signal: controller?.signal,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // The API answers both "your credentials are bad" and "your role
          // lacks this scope" with a 403 - DRF takes its challenge header from
          // ApiKeyAuthentication, which deliberately declines to send one. The
          // two are told apart by the body: DRF renders {"detail": ...} for an
          // authentication failure, while a scope denial comes back in the
          // app's own {"response": ...} envelope. Only the former is worth
          // re-authenticating for; re-logging in as the same user would just
          // loop on the latter.
          const body = await response.json().catch(() => null);
          if (body?.detail) {
            handleAuthFailure();
            throw new Error('Your session has expired. Redirecting you to sign in again.');
          }
          throw new Error(body?.response?.message || "You don't have access to this resource");
        }
        throw new Error('Something went wrong');
      }
      return response.json();
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw err;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };
  return fetcher;
}

export class MissingIdError extends Error {
  constructor(idName = 'id') {
    super(`${idName} is required to invalidate queries after a mutation`);
    this.name = `Missing${idName.charAt(0).toUpperCase() + idName.slice(1)}Error`;
  }
}
